// fetchStoredPaymentMethods.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { PaymentMethodUsage } from '../resource/shopConstants.js';
import getMeData from '../selector/getMeData.js';
import setMeData from '../action/setMeData.js';
import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch stored payment methods
 * @kind action
 * @param {PaymentMethodUsage} {[usage = PaymentMethodUsage.ALL]} - filter payment methods by usage.
 * @param {string} {[version = '2']} - version flag.
 * @param {string} {[currency]} - currency.
 * @return {Promise} Action promise.
 * @see {@link https://github.com/swaglive/discussions/issues/12}
 */
const fetchStoredPaymentMethods =
  ({ usage = PaymentMethodUsage.ALL, version = '2', currency } = {}) =>
  async (dispatch, getState) => {
    const selectPath = ['me', 'paymentMethods', usage];
    const token = getMeData(getState(), 'token');
    if (!token) {
      return dispatch({ type: '' });
    }
    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };
    const url = getResourceUrl({ endpoint: '/me/payment-methods' });
    url.searchParams.set('v', version);
    if (usage && PaymentMethodUsage.ALL !== usage) {
      url.searchParams.append('usage', usage);
    }
    if (currency) {
      url.searchParams.set('currency', currency);
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const payload = await response.json();
      const paymentMethods = payload.reduce(
        (cache, paymentMethod) => ({
          ...cache,
          [paymentMethod.id]: {
            ...paymentMethod,
            // TODO: use getPaymentSourceJointId
            jointId: `${paymentMethod.id}:${paymentMethod.type}:${paymentMethod.via}`,
          },
        }),
        {}
      );
      dispatch(
        setMeData({
          selectPath: 'paymentMethods',
          value: {
            [usage]: paymentMethods,
          },
        })
      );

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchStoredPaymentMethods;
